import { c as _c } from "react/compiler-runtime";
import { forwardRef, useEffect, useState, useRef } from 'react';
import type { MouseEvent, ReactNode, ReactElement } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { styled, SxProps } from '../styles';
import Box from '../Box';
import Typography from '../Typography';
const Transition = forwardRef<unknown, TransitionProps & {
  children: ReactElement<unknown, any>;
}>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <Slide direction="left" ref={ref} {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
const RootDialog = styled(Dialog, {
  shouldForwardProp: prop => prop !== 'isSide'
})<{
  isSide?: boolean;
}>(({
  theme: {
    breakpoints
  },
  isSide
}) => ({
  [breakpoints.up('md')]: /* istanbul ignore next */isSide && {
    width: 640,
    left: 'auto'
  }
}));
const Header = styled(DialogTitle)(({
  theme: {
    spacing,
    palette,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing(1),
  minHeight: 56,
  alignItems: 'center',
  padding: spacing(0, 2),
  borderBottom: `1px solid ${palette.layout.line.divider}`,
  [breakpoints.up('sm')]: {
    minHeight: 64,
    padding: spacing(0, 4)
  }
}));
const ActionContainer = styled('div')({
  flexShrink: 0
});
type Variant = 'default' | 'noPadding';
type AlignContent = 'center' | 'left' | 'fullWidth';
type Background = 'normal' | 'alternative';
type CloseReason = Parameters<NonNullable<DialogProps['onClose']>>[1] | 'backbuttonClick';
const ScrollContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['variant', 'backgroundVariant'].includes(prop)
})<{
  variant?: Variant;
  backgroundVariant?: Background;
}>(/* istanbul ignore next */
({
  theme: {
    spacing,
    breakpoints,
    palette
  },
  variant,
  backgroundVariant = 'normal'
}) => ({
  position: 'relative',
  padding: variant !== 'noPadding' ? spacing(4, 2) : 0,
  height: 'calc(100% - 64px)',
  overflowY: 'auto',
  backgroundColor: {
    normal: palette.layout.background.navigation,
    alternative: palette.layout.background.navigationAlternative
  }[backgroundVariant],
  [breakpoints.up('sm')]: {
    padding: variant !== 'noPadding' ? spacing(4, 4) : 0
  }
}));
const Container = styled('div', {
  shouldForwardProp: (prop: string) => !['variant', 'alignContent'].includes(prop)
})<{
  alignContent: AlignContent;
}>(({
  alignContent,
  theme: {
    spacing
  }
}) => ({
  minHeight: 'calc(100% - 64px)',
  maxWidth: 768,
  ...{
    center: {
      margin: '0 auto',
      width: '100%'
    },
    left: {
      marginLeft: spacing(2)
    },
    fullWidth: {
      maxWidth: '100%'
    }
  }[alignContent]
}));
export interface Props {
  children?: ReactNode;
  title?: string;
  onClose?: (event: Parameters<NonNullable<DialogProps['onClose']>>[0], reason: CloseReason) => void;
  onEndClose?: () => void;
  isSide?: boolean;
  isOpen?: boolean;
  variant?: Variant;
  alignContent?: AlignContent;
  background?: Background;
  isBackdropHidden?: boolean;
  translations: {
    closeButtonLabel: string;
  };
  isKeepMounted?: boolean;
  shouldForceDisplayBack?: boolean;
  actionElement?: ReactNode;
  'aria-labelledby'?: string;
}

/* istanbul ignore next */
const noop = () => {};
const ContentDialog = t0 => {
  const $ = _c(45);
  const {
    children,
    title,
    translations,
    variant,
    alignContent: t1,
    background: t2,
    actionElement,
    isOpen: t3,
    isSide,
    isBackdropHidden,
    isKeepMounted,
    shouldForceDisplayBack: t4,
    onClose: t5,
    onEndClose: t6,
    "aria-labelledby": ariaLabelledBy
  } = t0;
  const alignContent = t1 === undefined ? "center" : t1;
  const background = t2 === undefined ? "normal" : t2;
  const isOpen = t3 === undefined ? false : t3;
  const shouldForceDisplayBack = t4 === undefined ? false : t4;
  const onClose = t5 === undefined ? noop : t5;
  const onEndClose = t6 === undefined ? noop : t6;
  const [recordedScrollHeight, setRecordedScrollHeight] = useState(null);
  const [shouldDisplayBackIcon, setShouldDisplayBackIcon] = useState(false);
  const dialogRoot = useRef(null);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => globalThis?.document.querySelectorAll(".MuiDialog-root:not([aria-hidden])").length > 1 && dialogRoot?.current?.hasAttribute("aria-hidden") === false;
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const getShouldDisplayBackIcon = t7;
  const isBackButton = shouldDisplayBackIcon || shouldForceDisplayBack;
  let t8;
  if ($[1] !== isBackButton) {
    t8 = isBackButton ? <KeyboardArrowLeftIcon /> : <CloseIcon />;
    $[1] = isBackButton;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const iconToDisplay = t8;
  let t9;
  if ($[3] !== actionElement) {
    t9 = actionElement ? <ActionContainer>{actionElement}</ActionContainer> : null;
    $[3] = actionElement;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const displayActionElement = t9;
  let t10;
  let t11;
  if ($[5] !== isOpen || $[6] !== recordedScrollHeight) {
    t10 = () => {
      if (!recordedScrollHeight && isOpen === true) {
        setRecordedScrollHeight(window.scrollY);
      }
      if (recordedScrollHeight && isOpen === false) {
        window.scrollTo(0, recordedScrollHeight);
      }
    };
    t11 = [recordedScrollHeight, isOpen];
    $[5] = isOpen;
    $[6] = recordedScrollHeight;
    $[7] = t10;
    $[8] = t11;
  } else {
    t10 = $[7];
    t11 = $[8];
  }
  useEffect(t10, t11);
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      fontWeight: "bold",
      flexGrow: 1
    };
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let titleSx = t12;
  if (isSide && title) {
    let t13;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        ...titleSx,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      };
      $[10] = t13;
    } else {
      t13 = $[10];
    }
    titleSx = t13;
  }
  const Title = title ? Header : "div";
  let t13;
  if ($[11] !== isBackButton || $[12] !== onClose) {
    t13 = event => {
      onClose?.(event, isBackButton ? "backbuttonClick" : "escapeKeyDown");
    };
    $[11] = isBackButton;
    $[12] = onClose;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const handleCloseButtonClick = t13;
  let t14;
  if ($[14] !== onEndClose) {
    t14 = {
      onTransitionStart() {
        setShouldDisplayBackIcon(getShouldDisplayBackIcon());
      },
      onExited() {
        onEndClose?.();
      }
    };
    $[14] = onEndClose;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== handleCloseButtonClick || $[17] !== iconToDisplay || $[18] !== translations.closeButtonLabel) {
    t15 = <div><IconButton aria-label={translations.closeButtonLabel} size="large" color="inherit" onClick={handleCloseButtonClick}>{iconToDisplay}</IconButton></div>;
    $[16] = handleCloseButtonClick;
    $[17] = iconToDisplay;
    $[18] = translations.closeButtonLabel;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== title || $[21] !== titleSx) {
    t16 = title && <Typography sx={titleSx}>{title}</Typography>;
    $[20] = title;
    $[21] = titleSx;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== Title || $[24] !== displayActionElement || $[25] !== t15 || $[26] !== t16) {
    t17 = <Title>{t15}{t16}{displayActionElement}</Title>;
    $[23] = Title;
    $[24] = displayActionElement;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== alignContent || $[29] !== children) {
    t18 = <Container alignContent={alignContent}>{children}</Container>;
    $[28] = alignContent;
    $[29] = children;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== background || $[32] !== t18 || $[33] !== variant) {
    t19 = <ScrollContainer variant={variant} backgroundVariant={background} data-scrollable-element={true}>{t18}</ScrollContainer>;
    $[31] = background;
    $[32] = t18;
    $[33] = variant;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== ariaLabelledBy || $[36] !== isBackdropHidden || $[37] !== isKeepMounted || $[38] !== isOpen || $[39] !== isSide || $[40] !== onClose || $[41] !== t14 || $[42] !== t17 || $[43] !== t19) {
    t20 = <RootDialog ref={dialogRoot} isSide={isSide} open={isOpen} fullScreen={true} onClose={onClose} TransitionComponent={Transition} TransitionProps={t14} hideBackdrop={isBackdropHidden} keepMounted={isKeepMounted} aria-labelledby={ariaLabelledBy}>{t17}{t19}</RootDialog>;
    $[35] = ariaLabelledBy;
    $[36] = isBackdropHidden;
    $[37] = isKeepMounted;
    $[38] = isOpen;
    $[39] = isSide;
    $[40] = onClose;
    $[41] = t14;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
};
export default ContentDialog;